import { Component, Inject, OnInit, DOCUMENT } from "@angular/core";
import { Meta, Title } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { MessageService } from "./modules/shared/message/message.service";
import { UserService } from "./services/user/user.service";
import { getUrlParamValue } from "./util/url.util";
import { TranslateService } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {

  constructor(private translate: TranslateService,
              private userService: UserService,
              private router: Router,
              @Inject(DOCUMENT) private dom: Document,
              private messageService: MessageService,
              private activatedRoute: ActivatedRoute,
              private titleService: Title,
              private metaService: Meta) {

    translate.setFallbackLang('en');

    let language = getUrlParamValue(window.location.search, "language");
    if (!language) {
      const locale = getUrlParamValue(window.location.search, "locale");
      // note that if an unknown locale is passed, we fall back to "en" (see "setFallbackLang" above)
      language = locale?.length > 1 ? locale.substring(0, 2).toLowerCase() : undefined;
    }

    if (language) {
      this.userService.languageOverride = language;
      if (this.userService.getUser()) {
        this.userService.setUser(this.userService.getUser(), false);
      } else {
        translate.use(language);
      }
    } else if (this.userService.getUser()?.language) {
      translate.use(this.userService.getUser().language);
    } else {
      const domain = window.location.host;
      if (domain.startsWith("en.")) {
        translate.use("en");
      } else if (domain.startsWith("nl.")) {
        translate.use("nl");
      } else {
        translate.use(translate.getBrowserLang());
      }
    }

    // update the html lang attribute if it's not the default (en)
    if (translate.getCurrentLang() !== "en") {
      const htmlElement: HTMLHtmlElement = this.dom.querySelector("html");
      htmlElement.setAttribute("lang", translate.getCurrentLang());
    }

    // don't add a favicon for Bizcuit
    const head = this.dom.getElementsByTagName("head")[0];
    const favicon = this.dom.createElement('link') as HTMLLinkElement;
    favicon.setAttribute("rel", "shortcut icon");
    favicon.setAttribute("href", `./assets/img/favicon/${this.isBizcuit() ? 'empty' : 'combidesk'}-favicon.png`);
    head.appendChild(favicon);
  }

  async ngOnInit() {
    if (this.isBizcuit()) {
      // Bizcuit doesn't have a title (showing an invisible character, otherwise the domain is shown in the tab)
      this.titleService.setTitle("‎");
      this.metaService.updateTag({ name: "robots", content: "none" })
    } else {
      if (!this.titleService.getTitle()) {
        // if no title was set, assume it's not a prerendered page, so add these dynamically
        this.titleService.setTitle(await firstValueFrom(this.translate.get("DEFAULT_PAGE_TITLE")));
        this.metaService.updateTag({
          name: "description",
          content: await firstValueFrom(this.translate.get("DEFAULT_PAGE_META_DESCRIPTION"))
        });
        this.metaService.updateTag({
          name: "keywords",
          content: await firstValueFrom(this.translate.get("DEFAULT_PAGE_META_KEYWORDS"))
        });
      }
      this.metaService.updateTag({ name: "robots", content: "index, follow" });

      this.router.events.subscribe(async (e) => {
        if (e instanceof NavigationEnd) {
          console.log("Navigated to " + e.url + (e.urlAfterRedirects !== e.url ? " (️redirected to " + e.urlAfterRedirects + ")" : ""));
          if (e.url.startsWith("/koppeling") || e.url.startsWith("/integration")) {
            // skip, because this is handled in our prerender script
          } else {
            // clear any active WARN/ERROR, because those are prolly no longer relevant for the new route
            if (this.messageService.hasActiveStickyMessage()) {
              this.messageService.hideMessage();
            }
            const rt = this.getChild(this.activatedRoute);
            const titleKey = rt.snapshot.data["title"];
            // set the page title, based on what's configured in the routes
            if (titleKey) {
              const title = await firstValueFrom(this.translate.get(titleKey));
              this.titleService.setTitle(title);
              // note: we could also have a different description per route, but using the title for now
              this.metaService.updateTag({
                name: "description",
                content: title
              });
            }

            // set the SEO altroutes if provided
            const altroutes = rt.snapshot.data["altroutes"];
            this.updateHreflang("nl", "nl", altroutes?.nl, e.url);
            this.updateHreflang("en", null, altroutes?.en, e.url);
            this.updateHreflang("x-default", null, altroutes?.en, e.url);
            this.updateCanonical();
          }
        }
      });
    }

    setTimeout(() => {
      const successMessageKey = getUrlParamValue(window.location.href, "successMessageKey");
      if (successMessageKey) {
        this.messageService.showSuccessMessage(this.translate.instant(successMessageKey));
      }
      const errorMessageKey = getUrlParamValue(window.location.href, "errorMessageKey");
      if (errorMessageKey) {
        this.messageService.showErrorMessage(this.translate.instant(errorMessageKey));
      }
    }, 500);
  }

  getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    } else {
      return activatedRoute;
    }
  }

  private isBizcuit(): boolean {
    const host = window.location.host;
    return host.indexOf("hub.") > -1 || host.indexOf("bizcuit") > -1;
  }

  private updateHreflang(lang: string, subdomain?: string, altroute?: string, path?: string) {
    const head = this.dom.getElementsByTagName('head')[0];
    let element: HTMLLinkElement = this.dom.querySelector(`link[hreflang='${lang}']`) || null;
    if (element == null) {
      element = this.dom.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', lang);
    const domain = window.location.host;
    // Use origin + pathname to strip query parameters from hreflang URLs
    const cleanUrl = window.location.origin + window.location.pathname;
    let href = subdomain
        ? cleanUrl.replace(domain, `${subdomain}.combidesk.com`)
        : cleanUrl.replace(domain, `combidesk.com`);
    // Strip query params from path before replacing (Angular URL may include them)
    const cleanPath = path?.split('?')[0];
    if (altroute) {
      href = href.replace(cleanPath, `/${altroute}`);
    }
    element.setAttribute('href', href);
  }

  // rel=”canonical” points to the clean URL without query parameters,
  // so Google treats ?appstore=ExactOnline variants as duplicates of the canonical page.
  private updateCanonical() {
    const head = this.dom.getElementsByTagName('head')[0];
    let element: HTMLLinkElement = this.dom.querySelector(`link[rel='canonical']`) || null;
    if (element == null) {
      element = this.dom.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    element.setAttribute('href', window.location.origin + window.location.pathname);
  }
}
